import { inject, Injectable, Signal } from '@angular/core';
import { RoleClient, RoleDto } from '@lib-ui/client';
import { merge, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { LoadingState, ServiceState } from '@lib-ui/lib/models';
import { toSignal } from '@angular/core/rxjs-interop';

interface RoleState extends ServiceState {
  role: Signal<RoleDto | null>;
}

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private readonly roleClient = inject(RoleClient);
  private readonly load$ = new Subject<void>();
  private readonly error$ = new Subject<Error | null>();
  private readonly clear$ = new Subject<void>();
  private readonly role$ = this.load$.pipe(
    switchMap(() => {
      return this.roleClient.getUserRole().pipe(
        catchError((error) => {
          this.error$.next(error);
          return of(null);
        }),
      );
    }),
    shareReplay(1),
  );

  private readonly status$ = merge(
    this.role$.pipe(map(() => LoadingState.Success)),
    this.error$.pipe(map(() => LoadingState.Error)),
    this.load$.pipe(map(() => LoadingState.Pending)),
  );
  private readonly clearableRole$ = merge(
    this.role$,
    this.clear$.pipe(map(() => null)),
  );
  private readonly role = toSignal(this.clearableRole$, { initialValue: null });
  private readonly status = toSignal(this.status$, { initialValue: LoadingState.Pending });
  private readonly error = toSignal(this.error$, { initialValue: null });

  public state: RoleState = {
    role: this.role,
    status: this.status,
    error: this.error,
  };

  public load() {
    this.load$.next();
  }

  public clearCachedRoles() {
    this.clear$.next();
  }
}
